import React, { useMemo, useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import ReactECharts from 'echarts-for-react';
import { MS_CONFIGS } from '../../../organisms/SecufireActionLogs/SecufireActionLogs';
import { translateMicroservice } from '../../../../utils/logTranslations';
import './ActivityInsightsChart.css';

const MS_COLORS_FALLBACK = {
  'historic-fire_service': '#be1622',
  'physical_model':        '#7C3AED',
  'dfci_service':          '#F97316',
  'vegetation_service':    '#16a34a',
  'weather_service':       '#64748B',
  'auth':                  '#0EA5E9',
  'default':               '#64748b',
};

const MIN_PX_PER_POINT = 28;

/*
 * ── Seuil "compact" étendu à 888px ──
 * Au-delà de la simple activation d'une légende scroll, ce seuil
 * réserve aussi plus d'espace vertical (grid.bottom) et un padding
 * au-dessus de la légende, pour garantir qu'elle ne touche jamais
 * l'axe X ni la courbe — jusqu'à 888px de large, la légende reste en
 * mode "scroll" avec flèches de défilement plutôt que de tenter de
 * tout afficher sur une ligne qui finirait par chevaucher le graphe.
 */
const COMPACT_BREAKPOINT = 888;
const TABLET_BREAKPOINT  = 1100;

const getMsColor = (ms) =>
  (ms.color || MS_COLORS_FALLBACK[ms.microservice] || MS_COLORS_FALLBACK.default).trim();

const getMsLabel = (msKey) =>
  MS_CONFIGS[msKey]?.label || translateMicroservice(msKey);

const useIsCompact = (breakpoint = COMPACT_BREAKPOINT) => {
  const [isCompact, setIsCompact] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isCompact;
};

const ActivityInsightsChart = forwardRef(({
  dailyTrend = [],
  microservices = [],
  startDate,
  endDate,
  viewMode = 'weekly',
}, ref) => {
  const chartRef = useRef(null);
  useImperativeHandle(ref, () => ({
    getDataURL: (opts) => chartRef.current?.getEchartsInstance?.()?.getDataURL(opts),
  }));

  const isCompact       = useIsCompact(COMPACT_BREAKPOINT);
  const isTabletOrBelow = useIsCompact(TABLET_BREAKPOINT);

  const allDates = useMemo(() => {
    if (!startDate && !endDate && dailyTrend.length === 0) return [];
    const parseLocal = (d) => {
      if (!d) return new Date();
      if (typeof d === 'string') {
        const [y, mo, day] = d.slice(0, 10).split('-').map(Number);
        return new Date(y, mo - 1, day);
      }
      return new Date(d);
    };
    const from = startDate ? parseLocal(startDate) : (dailyTrend.length > 0 ? parseLocal(dailyTrend[0].date) : new Date());
    const to   = endDate   ? parseLocal(endDate)   : (dailyTrend.length > 0 ? parseLocal(dailyTrend[dailyTrend.length - 1].date) : new Date());
    from.setHours(0, 0, 0, 0);
    to.setHours(0, 0, 0, 0);
    const dates = [];
    const cursor = new Date(from);
    while (cursor <= to) { dates.push(new Date(cursor)); cursor.setDate(cursor.getDate() + 1); }
    return dates;
  }, [startDate, endDate, dailyTrend]);

  const dataByDate = useMemo(() => {
    const map = {};
    dailyTrend.forEach((d) => {
      const key = typeof d.date === 'string' ? d.date.slice(0, 10) : new Date(d.date).toISOString().slice(0, 10);
      map[key] = d;
    });
    return map;
  }, [dailyTrend]);

  const aggregated = useMemo(() => {
    if (allDates.length === 0) return { labels: [], seriesData: {} };
    const groups = {};
    allDates.forEach((date) => {
      let key;
      if (viewMode === 'daily') {
        key = date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
      } else if (viewMode === 'weekly') {
        const d = new Date(date); const day = d.getDay() || 7;
        d.setDate(d.getDate() - day + 1);
        key = `S. ${d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}`;
      } else {
        key = date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
      }
      const localKey = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
      const total = dataByDate[localKey]?.total_activities || 0;
      if (!groups[key]) {
        groups[key] = { total: 0, byMs: {} };
        microservices.forEach((ms) => { groups[key].byMs[ms.microservice] = 0; });
      }
      groups[key].total += total;
      const totalMsUsage = microservices.reduce((s, ms) => s + ms.total_usage, 0);
      microservices.forEach((ms) => {
        const ratio = totalMsUsage > 0 ? ms.total_usage / totalMsUsage : 0;
        groups[key].byMs[ms.microservice] += Math.round(total * ratio);
      });
    });
    const labels = Object.keys(groups);
    const seriesData = {};
    microservices.forEach((ms) => {
      seriesData[ms.microservice] = labels.map((l) => groups[l].byMs[ms.microservice] || 0);
    });
    return { labels, seriesData };
  }, [allDates, dataByDate, microservices, viewMode]);

  const chartWidth = useMemo(() => {
    if (viewMode !== 'daily') return '100%';
    const pts = aggregated.labels.length;
    const perPoint = isCompact ? MIN_PX_PER_POINT * 0.75 : MIN_PX_PER_POINT;
    const minWidth = pts * perPoint + (isCompact ? 60 : 100);
    return minWidth;
  }, [aggregated.labels.length, viewMode, isCompact]);

  const xAxisName = viewMode === 'daily' ? 'Date' : viewMode === 'weekly' ? 'Semaine' : 'Mois';

  const option = useMemo(() => ({
    grid: {
      left: isCompact ? 38 : 58,
      right: isCompact ? 10 : 20,
      top: 20,
      /*
       * Bottom augmenté (+8px sur chaque palier) : c'est cet espace
       * qui sépare les labels de l'axe X de la légende en dessous.
       * Combiné au padding ajouté sur la légende elle-même, ça
       * garantit une vraie marge, plus de chevauchement possible.
       */
      bottom: isCompact ? 74 : 60,
    },
    xAxis: {
      type: 'category',
      data: aggregated.labels,
      name: isCompact ? '' : xAxisName,
      nameLocation: 'end',
      nameTextStyle: { color: '#94a3b8', fontSize: 10, fontWeight: 600, padding: [0, 0, 0, 8] },
      axisLabel: {
        color: '#64748b',
        fontSize: isCompact ? 8 : 10,
        rotate: aggregated.labels.length > (isCompact ? 8 : 20) ? 35 : 0,
      },
      axisLine: { lineStyle: { color: '#e2e8f0' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: isCompact ? '' : 'Activités',
      nameLocation: 'end',
      nameTextStyle: { color: '#94a3b8', fontSize: 10, fontWeight: 600, padding: [0, 0, 6, 0] },
      axisLabel: { show: !isTabletOrBelow, color: '#64748b', fontSize: isCompact ? 8 : 10 },
      splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
      axisLine: { show: false },
    },
    legend: {
      data: microservices.map((ms) => getMsLabel(ms.microservice)),
      bottom: isCompact ? 2 : 0,
      left: isCompact ? 'center' : 58,
      orient: 'horizontal',
      type: isCompact ? 'scroll' : undefined,
      /* Espace tampon entre les labels de l'axe X et la légende. */
      padding: isCompact ? [10, 0, 0, 0] : [8, 0, 0, 0],
      textStyle: { color: '#475569', fontSize: isCompact ? 9 : 11, fontWeight: 500 },
      itemWidth: isCompact ? 12 : 18,
      itemHeight: isCompact ? 6 : 8,
      icon: 'roundRect',
      itemGap: isCompact ? 8 : 14,
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255,255,255,0.97)',
      borderColor: '#e2e8f0', borderWidth: 1,
      textStyle: { color: '#1e293b', fontSize: 12 },
      axisPointer: { type: 'cross', lineStyle: { type: 'dashed', color: '#cbd5e1' } },
      formatter: (params) => {
        let html = `<div style="font-weight:700;margin-bottom:6px">${params[0].axisValue}</div>`;
        params.forEach((p) => {
          html += `<div style="display:flex;align-items:center;gap:8px;margin-bottom:3px">
            <span style="width:10px;height:10px;border-radius:2px;background:${p.color};display:inline-block"></span>
            <span style="color:#64748b">${p.seriesName}</span>
            <strong style="margin-left:auto;padding-left:12px">${p.value}</strong></div>`;
        });
        return html;
      },
    },
    series: microservices.map((ms) => {
      const color = getMsColor(ms);
      return {
        name: getMsLabel(ms.microservice),
        type: 'line',
        data: aggregated.seriesData[ms.microservice] || [],
        smooth: 0.4,
        symbol: 'circle', symbolSize: isCompact ? 3.5 : 5,
        lineStyle: { width: isCompact ? 1.75 : 2.5, color },
        itemStyle: { color, borderWidth: 2, borderColor: '#fff' },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: color + '33' },
              { offset: 1, color: 'rgba(255,255,255,0)' },
            ],
          },
        },
        emphasis: { focus: 'series', lineStyle: { width: 3.5 } },
      };
    }),
  }), [aggregated, microservices, xAxisName, isCompact, isTabletOrBelow]);

  return (
    <div className="aic-wrapper">
      <div className="aic-scroll-outer">
        <div className="aic-scroll-inner" style={{ width: chartWidth, minWidth: '100%' }}>
          <ReactECharts
            ref={chartRef}
            option={option}
            style={{ height: '100%', width: '100%' }}
            notMerge={true}
            lazyUpdate={false}
          />
        </div>
      </div>
    </div>
  );
});

export default ActivityInsightsChart;